import { setup, assign, fromPromise } from 'xstate';
import { supabase } from '@/integrations/supabase/client';
import { EntityConfig, EntityMachineContext } from '@/config/entities/types';

// Helper to execute Supabase queries with proper typing
async function executeQuery<T>(
  tableName: string,
  queryFn: (query: any) => any
): Promise<{ data: T[] | null; error: any; count: number | null }> {
  const baseQuery = supabase.from(tableName as any);
  return queryFn(baseQuery);
}

// Create the entity machine factory
export function createEntityMachine<T extends Record<string, any>>(config: EntityConfig<T>) {
  return setup({
    types: {
      context: {} as EntityMachineContext<T>,
      events: {} as 
        | { type: 'FETCH' }
        | { type: 'CREATE'; data: Partial<T> }
        | { type: 'UPDATE'; id: string; data: Partial<T> }
        | { type: 'DELETE'; id: string }
        | { type: 'BULK_DELETE'; ids: string[] }
        | { type: 'SELECT'; item: T | null }
        | { type: 'SET_FILTERS'; filters: Record<string, any> }
        | { type: 'SET_ADVANCED_FILTERS'; advancedFilters: any[] }
        | { type: 'SET_SEARCH'; query: string }
        | { type: 'SET_PAGE'; page: number }
        | { type: 'SET_PAGE_SIZE'; pageSize: number }
        | { type: 'REFRESH' }
        | { type: 'RETRY' }
        | { type: 'DISMISS_ERROR' },
    },
    actors: {
      fetchData: fromPromise(async ({ input }: { input: { config: EntityConfig<T>; filters: Record<string, any>; searchQuery: string; pagination: { page: number; pageSize: number }; advancedFilters?: any[] } }) => {
        const { config, filters, searchQuery, pagination, advancedFilters } = input;
        
        let query = (supabase.from as any)(config.tableName)
          .select(config.defaultSelect || '*', { count: 'exact' });
        
        // Apply basic filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value !== 'all' && value !== '') {
            if (value === 'true') {
              query = query.eq(key, true);
            } else if (value === 'false') {
              query = query.eq(key, false);
            } else {
              query = query.eq(key, value);
            }
          }
        });

        // Apply advanced filters
        if (advancedFilters && advancedFilters.length > 0) {
          for (const filter of advancedFilters) {
            if (!filter.value || (Array.isArray(filter.value) && filter.value.length === 0)) continue;

            switch (filter.operator) {
              case 'eq':
                query = query.eq(filter.field, filter.value);
                break;
              case 'neq':
                query = query.neq(filter.field, filter.value);
                break;
              case 'contains':
                query = query.ilike(filter.field, `%${filter.value}%`);
                break;
              case 'in':
                // For multi-select with related table (like skills)
                if (filter.field === 'skills') {
                  // This needs special handling - we'll filter in memory or use RPC
                  // For now, we skip this and handle it in the component
                } else {
                  query = query.in(filter.field, filter.value);
                }
                break;
              case 'gt':
                query = query.gt(filter.field, filter.value);
                break;
              case 'lt':
                query = query.lt(filter.field, filter.value);
                break;
              case 'gte':
                query = query.gte(filter.field, filter.value);
                break;
              case 'lte':
                query = query.lte(filter.field, filter.value);
                break;
            }
          }
        }
        
        // Apply search
        if (searchQuery && config.searchFields && config.searchFields.length > 0) {
          const searchConditions = config.searchFields
            .map(field => `${field}.ilike.%${searchQuery}%`)
            .join(',');
          query = query.or(searchConditions);
        }
        
        // Apply ordering
        if (config.defaultOrderBy) {
          query = query.order(config.defaultOrderBy.field, { ascending: config.defaultOrderBy.ascending });
        }
        
        // Apply pagination
        const from = (pagination.page - 1) * pagination.pageSize;
        const to = from + pagination.pageSize - 1;
        query = query.range(from, to);
        
        const { data, error, count } = await query;
        
        if (error) throw error;
        
        return { items: (data || []) as T[], total: count || 0 };
      }),
      
      createItem: fromPromise(async ({ input }: { input: { config: EntityConfig<T>; data: Partial<T> } }) => {
        const { config, data } = input;
        let createData = data;
        
        // Run beforeCreate hook
        if (config.hooks?.beforeCreate) {
          createData = await config.hooks.beforeCreate(createData);
        }
        
        const { data: created, error } = await (supabase.from as any)(config.tableName)
          .insert([createData])
          .select()
          .single();
        
        if (error) throw error;
        
        // Run afterCreate hook
        if (config.hooks?.afterCreate) {
          await config.hooks.afterCreate(created as T);
        }
        
        return created as T;
      }),
      
      updateItem: fromPromise(async ({ input }: { input: { config: EntityConfig<T>; id: string; data: Partial<T> } }) => {
        const { config, id, data } = input;
        let updateData = data;
        
        // Get old data for hooks and audit
        const { data: oldData } = await (supabase.from as any)(config.tableName)
          .select('*')
          .eq(config.keyField, id)
          .single();
        
        // Run beforeUpdate hook
        if (config.hooks?.beforeUpdate && oldData) {
          updateData = await config.hooks.beforeUpdate(id, updateData, oldData as T);
        }
        
        const { data: updated, error } = await (supabase.from as any)(config.tableName)
          .update(updateData)
          .eq(config.keyField, id)
          .select()
          .single();
        
        if (error) throw error;
        
        // Run afterUpdate hook
        if (config.hooks?.afterUpdate && oldData) {
          await config.hooks.afterUpdate(updated as T, oldData as T);
        }
        
        return { updated: updated as T, oldData: oldData as T };
      }),
      
      deleteItem: fromPromise(async ({ input }: { input: { config: EntityConfig<T>; id: string } }) => {
        const { config, id } = input;
        
        // Get item for hooks
        const { data: itemToDelete } = await (supabase.from as any)(config.tableName)
          .select('*')
          .eq(config.keyField, id)
          .single();
        
        // Run beforeDelete hook
        if (config.hooks?.beforeDelete && itemToDelete) {
          const canDelete = await config.hooks.beforeDelete(itemToDelete as T);
          if (!canDelete) throw new Error('Delete prevented by hook');
        }
        
        const { error } = await (supabase.from as any)(config.tableName)
          .delete()
          .eq(config.keyField, id);
        
        if (error) throw error;
        
        // Run afterDelete hook
        if (config.hooks?.afterDelete && itemToDelete) {
          await config.hooks.afterDelete(itemToDelete as T);
        }
        
        return itemToDelete as T;
      }),
      
      bulkDeleteItems: fromPromise(async ({ input }: { input: { config: EntityConfig<T>; ids: string[] } }) => {
        const { config, ids } = input;
        
        const { error } = await (supabase.from as any)(config.tableName)
          .delete()
          .in(config.keyField, ids);
        
        if (error) throw error;
        
        return { ids };
      }),
    },
    actions: {
      setLoading: assign({ loading: true }),
      clearLoading: assign({ loading: false }),
      setError: assign({ 
        error: (_, params: { error: unknown }) => {
          const err = params.error as Error;
          return err?.message || 'An error occurred';
        }
      }),
      clearError: assign({ error: null }),
      setItems: assign({
        items: (_, params: { items: T[] }) => params.items,
      }),
      setTotal: assign({
        pagination: ({ context }, params: { total: number }) => ({
          ...context.pagination,
          total: params.total,
        }),
      }),
      selectItem: assign({ 
        selectedItem: (_, params: { item: T | null }) => params.item 
      }),
      setFilters: assign({ 
        filters: (_, params: { filters: Record<string, any> }) => params.filters,
        pagination: ({ context }) => ({ ...context.pagination, page: 1 }),
      }),
      setSearch: assign({ 
        searchQuery: (_, params: { query: string }) => params.query,
        pagination: ({ context }) => ({ ...context.pagination, page: 1 }),
      }),
      setPage: assign({ 
        pagination: ({ context }, params: { page: number }) => ({ ...context.pagination, page: params.page }) 
      }),
      setPageSize: assign({ 
        pagination: ({ context }, params: { pageSize: number }) => ({ 
          ...context.pagination, 
          pageSize: params.pageSize,
          page: 1,
        }) 
      }),
      setAdvancedFilters: assign({ 
        advancedFilters: (_, params: { advancedFilters: any[] }) => params.advancedFilters,
        pagination: ({ context }) => ({ ...context.pagination, page: 1 }),
      }),
    },
  }).createMachine({
    id: `entity-${config.name}`,
    initial: 'idle',
    context: {
      items: [],
      selectedItem: null,
      loading: false,
      error: null,
      filters: {},
      advancedFilters: [],
      searchQuery: '',
      pagination: { 
        page: 1, 
        pageSize: config.defaultPageSize || 25, 
        total: 0 
      },
      config,
    },
    states: {
      idle: {
        on: {
          FETCH: 'fetching',
          REFRESH: 'fetching',
          CREATE: 'creating',
          UPDATE: 'updating',
          DELETE: 'deleting',
          BULK_DELETE: 'bulkDeleting',
          SELECT: {
            actions: [{ type: 'selectItem', params: ({ event }) => ({ item: event.item }) }],
          },
          SET_FILTERS: {
            target: 'fetching',
            actions: [{ type: 'setFilters', params: ({ event }) => ({ filters: event.filters }) }],
          },
          SET_SEARCH: {
            target: 'fetching',
            actions: [{ type: 'setSearch', params: ({ event }) => ({ query: event.query }) }],
          },
          SET_PAGE: {
            target: 'fetching',
            actions: [{ type: 'setPage', params: ({ event }) => ({ page: event.page }) }],
          },
          SET_PAGE_SIZE: {
            target: 'fetching',
            actions: [{ type: 'setPageSize', params: ({ event }) => ({ pageSize: event.pageSize }) }],
          },
          SET_ADVANCED_FILTERS: {
            target: 'fetching',
            actions: [{ type: 'setAdvancedFilters', params: ({ event }) => ({ advancedFilters: event.advancedFilters }) }],
          },
        },
      },
      
      fetching: {
        entry: 'setLoading',
        invoke: {
          src: 'fetchData',
          input: ({ context }) => ({
            config: context.config,
            filters: context.filters,
            advancedFilters: context.advancedFilters,
            searchQuery: context.searchQuery,
            pagination: context.pagination,
          }),
          onDone: {
            target: 'idle',
            actions: [
              'clearLoading',
              'clearError',
              { type: 'setItems', params: ({ event }) => ({ items: event.output.items }) },
              { type: 'setTotal', params: ({ event }) => ({ total: event.output.total }) },
            ],
          },
          onError: {
            target: 'error',
            actions: [
              'clearLoading',
              { type: 'setError', params: ({ event }) => ({ error: event.error }) },
            ],
          },
        },
      },
      
      creating: {
        entry: 'setLoading',
        invoke: {
          src: 'createItem',
          input: ({ context, event }) => ({
            config: context.config,
            data: (event as { type: 'CREATE'; data: Partial<T> }).data,
          }),
          onDone: {
            target: 'fetching',
            actions: ['clearLoading'],
          },
          onError: {
            target: 'error',
            actions: [
              'clearLoading',
              { type: 'setError', params: ({ event }) => ({ error: event.error }) },
            ],
          },
        },
      },
      
      updating: {
        entry: 'setLoading',
        invoke: {
          src: 'updateItem',
          input: ({ context, event }) => ({
            config: context.config,
            id: (event as { type: 'UPDATE'; id: string; data: Partial<T> }).id,
            data: (event as { type: 'UPDATE'; id: string; data: Partial<T> }).data,
          }),
          onDone: {
            target: 'fetching',
            actions: ['clearLoading'],
          },
          onError: {
            target: 'error',
            actions: [
              'clearLoading',
              { type: 'setError', params: ({ event }) => ({ error: event.error }) },
            ],
          },
        },
      },
      
      deleting: {
        entry: 'setLoading',
        invoke: {
          src: 'deleteItem',
          input: ({ context, event }) => ({
            config: context.config,
            id: (event as { type: 'DELETE'; id: string }).id,
          }),
          onDone: {
            target: 'fetching',
            actions: ['clearLoading'],
          },
          onError: {
            target: 'error',
            actions: [
              'clearLoading',
              { type: 'setError', params: ({ event }) => ({ error: event.error }) },
            ],
          },
        },
      },
      
      bulkDeleting: {
        entry: 'setLoading',
        invoke: {
          src: 'bulkDeleteItems',
          input: ({ context, event }) => ({
            config: context.config,
            ids: (event as { type: 'BULK_DELETE'; ids: string[] }).ids,
          }),
          onDone: {
            target: 'fetching',
            actions: ['clearLoading'],
          },
          onError: {
            target: 'error',
            actions: [
              'clearLoading',
              { type: 'setError', params: ({ event }) => ({ error: event.error }) },
            ],
          },
        },
      },
      
      error: {
        on: {
          RETRY: 'fetching',
          DISMISS_ERROR: {
            target: 'idle',
            actions: 'clearError',
          },
          FETCH: 'fetching',
        },
      },
    },
  });
}
